// To book › Leads, what it does: the leads of one status a page at a time (leads.list, "Load more"),
// the open count the server's total, and what was just done (a lead added or closed). Riverstone's
// section (Leads.tsx) and Blueprint's (LeadsPart.tsx) draw it; their forms report to added and closed.
import { useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { addRows } from '../blueprint/paged.ts';
import type { Lead, LeadStatus } from './tobook-model.ts';

/** Which leads are listed: the open ones first. */
export const LEAD_TABS: { status: LeadStatus; label: string }[] = [
  { status: 'open', label: 'Open' },
  { status: 'booked', label: 'Booked' },
  { status: 'closed', label: 'Closed' },
];

/** How many leads a page holds. */
const LEADS_PAGE = 50;
type LeadPage = { leads: Lead[]; total: number; next: string | null };

export function useLeads(onChanged: () => void) {
  const [status, setStatus] = useState<LeadStatus>('open');
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [total, setTotal] = useState(0);
  const [next, setNext] = useState<string | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [said, setSaid] = useState<string | null>(null);
  const page = (after?: string) =>
    client.call<LeadPage>('leads.list', { status, limit: LEADS_PAGE, ...(after ? { after } : {}) });
  // a page that comes back for a list no longer shown (another tab, or read again) is dropped
  const reads = useRef(0);
  useEffect(() => {
    const read = ++reads.current;
    setLeads(null);
    page()
      .then((r) => {
        if (read !== reads.current) return;
        setLeads(r.leads);
        setTotal(r.total);
        setNext(r.next);
        setError(null);
      })
      .catch((e: unknown) => read === reads.current && setError(errorText(e, 'Could not load the leads.')));
  }, [status, tick]);
  const more = () => {
    if (!next) return;
    const read = reads.current;
    setMoreBusy(true);
    page(next)
      .then((r) => {
        if (read !== reads.current) return;
        setLeads((l) => addRows(l ?? [], r.leads));
        setTotal(r.total);
        setNext(r.next);
      })
      .catch((e: unknown) => read === reads.current && setError(errorText(e, 'Could not load the rest.')))
      .finally(() => setMoreBusy(false));
  };
  const done = (m: string) => {
    setSaid(m);
    setTick((t) => t + 1);
    onChanged();
  };
  /** A lead was just recorded: the open ones, with it, and what was done. */
  const added = (l: Lead) => {
    setStatus('open');
    done(`Lead recorded${l.name ? ` for ${l.name}` : ''}. Book it when they’re ready.`);
  };
  const closed = (l: Lead) => done(`Closed ${l.name ?? 'the lead'} with no job.`);
  return { status, setStatus, leads, total, next, moreBusy, error, said, more, added, closed };
}

/** The leads' empty line, for the status shown. */
export const noLeads = (status: LeadStatus) =>
  status === 'open'
    ? 'No open leads: every request is booked or closed.'
    : status === 'booked'
      ? 'No booked leads yet.'
      : 'No closed leads.';
